import type { PostCardData } from '@eliadmin/core/src/components/dlz/blog/post-card-data';

import { Badge } from '@eliadmin/core/src/components/dlz/ui/badge';
import { CmsImage } from '@eliadmin/core/src/components/dlz/ui/cms-image';
import { Icon } from '@eliadmin/core/src/components/dlz/ui/icon';
import Link from '@eliadmin/core/src/components/link';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { formatContentDate } from '@eliadmin/core/src/utils/date-utils';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { contentDateTime } from '@eliadmin/core/src/utils/dlz/text';

type PostCardProps = {
  post: PostCardData;
  locale: string;
  /** „Prečítať článok“ from the administration; the whole card is the link to the article. */
  readMore: string;
  heading?: 'h2' | 'h3';
  imageSizes: string;
  /** Photo box override – the homepage teaser keeps the 240 px / radius 8 photo on phones too. */
  imageClassName?: string;
  className?: string;
};

/**
 * Article card of the blog (Figma „Blog · …“ 60391:78442, phones „Article · …“ 60460:110257): the
 * photo with the category badge – 240 px high and radius 8 on desktop, 358 : 240 and radius 18 on
 * phones – the date, the title (two lines, DLZ/Heading/H5), a two-line excerpt and „Prečítať
 * článok“. The title link covers the card. The reading time of the design has no API source.
 */
export function PostCard({
  post,
  locale,
  readMore,
  heading: Title = 'h3',
  imageSizes,
  imageClassName,
  className,
}: PostCardProps) {
  const date = formatContentDate(post.createdAt, locale);

  return (
    <article className={cn('relative flex flex-col gap-4', className)}>
      <div
        className={cn(
          'relative flex aspect-[358/240] items-start overflow-hidden rounded-[18px] bg-[#E2E4E7] p-4 lg:aspect-auto lg:h-60 lg:rounded-[8px]',
          imageClassName,
        )}
      >
        <CmsImage image={post.image} decorative sizes={imageSizes} />
        {post.category ? (
          <Badge tone="new" className="relative">
            {post.category}
          </Badge>
        ) : null}
      </div>
      <div className="flex flex-col gap-3">
        {date ? (
          <time dateTime={contentDateTime(post.createdAt)} className="dlz-caption text-[#6B7079]">
            {date}
          </time>
        ) : null}
        <div className="flex flex-col gap-3 lg:gap-2">
          {/* The design's 52 px title box (cards side by side from `md`); the clamped heading
              itself stays two lines high, so no third line peeks out of the box. */}
          <div className="md:h-[52px]">
            <Title className="dlz-h5 line-clamp-2 text-[#0F1012]">
              <Link
                href={ROUTES.BLOG_DETAIL({ slug: post.slug })}
                className="after:absolute after:inset-0 hover:underline focus-visible:outline-none focus-visible:after:rounded-[8px] focus-visible:after:ring-2 focus-visible:after:ring-[#0F1012]"
              >
                {post.name}
              </Link>
            </Title>
          </div>
          {post.excerpt ? (
            <p className="dlz-body-s line-clamp-2 h-10 text-[#6B7079]">{post.excerpt}</p>
          ) : null}
        </div>
      </div>
      {readMore ? (
        <span
          aria-hidden="true"
          className="dlz-body-m-medium mt-auto inline-flex items-center gap-1 text-[#D7191F]"
        >
          {readMore}
          <Icon name="caret-right" size={20} />
        </span>
      ) : null}
    </article>
  );
}
